import * as React from 'react';

import { cn, TouchSelect, TouchSelectProps } from '@rematter/ui-web';
import startCase from 'lodash/startCase';

type TouchSelectEnumProps<T extends string | number = string> = Omit<
  TouchSelectProps<T>,
  'options' | 'isLoading' | 'value' | 'onChange'
> & {
  value: T;
  onChange?: (value: T | undefined, entity: T) => void;
  enumValue: Record<string, string>;
  showAllOption?: boolean;
  allOptionLabel?: string;
  formatOptionLabel?: (value: T) => string;
};

export const TouchSelectEnum = <T extends string | number = string>({
  onChange,
  showAllOption,
  allOptionLabel,
  value,
  enumValue,
  formatOptionLabel,
  className,
  ...props
}: TouchSelectEnumProps<T>) => {
  const options = React.useMemo(
    () => [
      ...(showAllOption
        ? [{ key: 'all' as T, label: allOptionLabel ?? 'All' }]
        : []),
      ...Object.values(enumValue).map((v) => ({
        key: v as T,
        label: formatOptionLabel?.(v as T) ?? startCase(v),
      })),
    ],
    [showAllOption, allOptionLabel, enumValue, formatOptionLabel],
  );

  const handleChange = React.useCallback(
    (value: T) => {
      const effectiveValue = (value as string) === 'all' ? undefined : value;
      onChange?.(effectiveValue, effectiveValue as T);
    },
    [onChange],
  );

  return (
    <TouchSelect<T>
      {...props}
      value={value ?? (showAllOption ? ('all' as T) : undefined)}
      onChange={handleChange}
      className={cn('@TouchSelectEnum', className)}
      options={options}
    />
  );
};
